import { type ComboboxItem, Select } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import cs from 'classnames';
import Link from 'next/link';
import { useRouter } from 'next/router';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Button } from '@/DesignSystem/Component';
import { LINKS } from '@/Module/Core/Constant';
import { formatPrice } from '@/Module/Core/Helper';
import { useAppSelector } from '@/Module/Store/Hook';
import { SubscriptionDescription } from '@/Module/Subscription/Component';
import type { ISubscriptionPlan } from '@/Module/Subscription/Model/DataModel';
import type { IStatisticsDataReq } from '@/Module/Workspace/Model/ApiModel';
interface ISubscriptionCartsProps {
  data: ISubscriptionPlan[];
}
export const BOX_CLASS_S: Record<string, string> = {
  green: 'border-greenFour ',
  blue: 'border-blueFour ',
  pink: 'border-pinkFour ',
  orange: 'border-orangeFour '
};
export const TITLE_COLOR_S: Record<string, string> = {
  blue: 'text-blueFour',
  pink: 'text-pinkFour',
  orange: 'text-orangeFive',
  green: 'text-greenFive '
};
export const BUTTON_COLOR_S: Record<string, string> = {
  green: 'text-greenFour border-greenFour hover:bg-greenFour hover:text-white hover:border-white',
  blue: 'text-blueFour border-blueFour hover:bg-blueFour hover:text-white hover:border-white',
  orange: 'text-orangeFive border-orangeFive hover:bg-orangeFive hover:text-white hover:border-white',
  pink: 'text-pinkFive border-pinkFive hover:bg-pinkFour hover:text-white hover:border-white'
};
const SubscriptionCarts: FC<ISubscriptionCartsProps> = ({
  data
}) => {
  const isLoggedIn = useAppSelector(state => state.auth.isLoggedIn);
  const router = useRouter();
  const [isSelect, setIsSelect] = useState(false);
  const {
    mainPlan,
    subPlan
  } = useMemo(() => {
    const result: Record<'mainPlan' | 'subPlan', ISubscriptionPlan | undefined> = {
      mainPlan: undefined,
      subPlan: undefined
    };
    result.mainPlan = data.find(item => item.duration === null);
    result.subPlan = data.find(item => item.duration !== null);
    return result;
  }, [data]);
  const [planActive, setPanelActive] = useState<ISubscriptionPlan | null>(mainPlan || null);
  const filterData: ComboboxItem[] = [{
    value: `${mainPlan?.id}`,
    label: 'پایان سال تحصیلی'
  }, {
    value: `${subPlan?.id}`,
    label: 'اشتراک یک ماهه'
  }];
  const dataChangeHandler = useCallback((id: IStatisticsDataReq['RecentDate']) => {
    setPanelActive(data.find(plan => plan.id === id) || null);
  }, [data]);
  if (!planActive) {
    return null;
  }
  return <div className={cs(BOX_CLASS_S[planActive.color], `rounded-md border-[1px] px-4 py-5 flex flex-col`)} data-sentry-component="SubscriptionCarts" data-sentry-source-file="SubscriptionCarts.tsx">
      <div className={cs(TITLE_COLOR_S[planActive.color], 'font-bold text-xl mb-4 md:mb-8')}>
        {planActive.subscriptionName.split('_')[0]}
      </div>
      {planActive.price.originalPrice ? <div className={cs('relative mx-auto text-blackFour w-fit text-center before:absolute before:top-[14px] before:bg-blackThree before:h-[1px] before:w-full')}>
          <span className="ml-1 text-lg font-semibold">{formatPrice(planActive.price.originalPrice)}</span>
          <span className={cs(` font-semibold `)}>تومان</span>
        </div> : <div className="h-[28px]" />}

      <div className="mb-3 flex  items-center justify-center gap-2 text-center">
        <span className="text-3xl font-extrabold">{formatPrice(planActive.price.price)}</span>
        <span className={cs(`text-lg text-blackFour font-semibold `)}>تومان</span>
      </div>
      <div className="flex flex-row items-center justify-center ">
        <span className="ml-2 text-xs text-blackThree sm:text-sm"> مدت اعتبار:</span>
        <Select onDropdownOpen={() => setIsSelect(true)} onDropdownClose={() => setIsSelect(false)} rightSection={<IconChevronDown className="mr-4" style={{
        width: 34,
        height: 34
      }} />} onChange={event => dataChangeHandler(Number(event) as IStatisticsDataReq['RecentDate'])} allowDeselect={false} classNames={{
        input: 'text-xs sm:text-sm text-right font-pelak text-blackThree p-0 pl-6 ',
        section: 'text-blackThree ',
        wrapper: 'max-w-[140px] w-full  sm:mt-0',
        dropdown: 'p-0 optionCustom border-[1px] border-blackTwo w-[250px]',
        option: ' py-3 bg-white text-blackThree text-xs sm:text-sm data-[checked=true]:bg-blueOne  data-[checked=true]:text-blueFour '
      }} variant="unstyled" data={filterData} defaultValue={String(planActive?.id) ?? '1'} data-sentry-element="Select" data-sentry-source-file="SubscriptionCarts.tsx" />
      </div>
      <div className="my-3 h-[1px] w-full bg-blackTwo sm:my-6" />
      <ul className={cs(isSelect && 'opacity-30', ' transition-all duration-200  sm:flex-1 h-auto flex-1 mb-8')}>
        <SubscriptionDescription subscriptionDescription={planActive.subscriptionDescription} data-sentry-element="SubscriptionDescription" data-sentry-source-file="SubscriptionCarts.tsx" />
      </ul>
      <Button href={isLoggedIn ? LINKS.SUBSCRIPTION.ITEM.replace('[id]', String(planActive.id)) : `${LINKS.AUTH.LOGIN}?redirectUrl=${encodeURI(router.asPath)}${planActive.id}`} as={Link} className={cs(isSelect && 'opacity-30', BUTTON_COLOR_S[planActive.color], 'w-full self-end border-[1px] font-bold transition-all duration-300 ')} data-sentry-element="Button" data-sentry-source-file="SubscriptionCarts.tsx">
        خرید اشتراک
      </Button>
    </div>;
};
export default SubscriptionCarts;